// export default Register;
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BsThreeDotsVertical } from "react-icons/bs";
import Form from "../components/Form";
import { supabase } from "../config";

const TZ = {
  CST: { key: "CST", label: "CST", iana: "America/Chicago" },
  IST: { key: "IST", label: "IST", iana: "Asia/Kolkata" },
};

// UTC time boundaries for each date's slots
const SLOT_RANGES = {
  "2026-03-21": {
    from: "2026-03-21T12:00:00+00:00",  // 7:00 AM CDT (UTC-5, Central local time)
    to:   "2026-03-22T00:00:00+00:00",  // 7:00 PM CDT exclusive (last slot at 6:56 PM)
  },
  "2026-03-22": {
    from: "2026-03-22T03:30:00+00:00",  // 9:00 AM IST (UTC+5:30)
    to:   "2026-03-22T18:06:00+00:00",  // 11:36 PM IST exclusive (last slot at 11:24 PM IST)
  },
};

const Register = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState("2026-03-21");
  const [slots, setSlots] = useState([]);
  const [storyTellers, setStoryTellers] = useState({});
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [tzKey, setTzKey] = useState("CST");
  const activeTZ = TZ[tzKey];

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAvailableOnly, setShowAvailableOnly] = useState(false);

  // Self-service edit/release state
  const [menuOpenSlotId, setMenuOpenSlotId] = useState(null);
  const [passwordPrompt, setPasswordPrompt] = useState(null); // { slotId, action: 'edit'|'release' }
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [editSlotId, setEditSlotId] = useState(null);
  const [editStorytellerData, setEditStorytellerData] = useState(null);

  const timeFormatter = useMemo(() => {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: activeTZ.iana,
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }, [activeTZ.iana]);

  const dateShortFormatter = useMemo(() => {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: activeTZ.iana,
      month: "short",
      day: "numeric",
    });
  }, [activeTZ.iana]);

  const fetchSlots = async (date, opts = { showLoader: true }) => {
    try {
      setErrorMsg("");
      if (opts.showLoader) setIsLoading(true);
      else setIsRefreshing(true);

      const range = SLOT_RANGES[date];
      const { data, error } = await supabase
        .from("slots")
        .select("slot_id, slot_from, slot_to, is_booked")
        .gte("slot_from", range.from)
        .lt("slot_from", range.to)
        .order("slot_from", { ascending: true });

      if (error) throw error;

      const list = data || [];
      setSlots(list);

      // reset selection if date changes / slot disappears
      setSelectedSlot((prev) => {
        if (!prev) return null;
        const exists = list.some((s) => s.slot_id === prev && !s.is_booked);
        return exists ? prev : null;
      });

      const bookedSlotIds = list.filter((s) => s.is_booked).map((s) => s.slot_id);
      await fetchStoryTellers(bookedSlotIds);
    } catch (err) {
      console.error("Error fetching slots:", err);
      setErrorMsg(err?.message || "Failed to load slots.");
      setSlots([]);
      setStoryTellers({});
      setSelectedSlot(null);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const fetchStoryTellers = async (bookedSlotIds) => {
    if (!bookedSlotIds || bookedSlotIds.length === 0) {
      setStoryTellers({});
      return;
    }

    const { data, error } = await supabase
      .from("storytellers")
      .select("slot_id, storyteller_telugu, storyteller_english, writer_story_title, introduction, whatsapp, email, photo_url, video_url, story_length_mins")
      .in("slot_id", bookedSlotIds);

    if (error) {
      console.error("Error fetching storytellers:", error);
      setStoryTellers({});
      return;
    }

    const storytellerMap = {};
    (data || []).forEach((teller) => {
      storytellerMap[teller.slot_id] = teller; // store full object
    });

    setStoryTellers(storytellerMap);
  };

  // Auto-switch timezone default when date changes
  useEffect(() => {
    // March 21 slots are natively CST, March 22 slots are natively IST
    if (selectedDate === "2026-03-21") setTzKey("CST");
    else if (selectedDate === "2026-03-22") setTzKey("IST");
    fetchSlots(selectedDate, { showLoader: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleSlotSelect = (slot) => {
    if (slot.is_booked) return;
    setSelectedSlot(slot.slot_id);
  };

  const formatTime = (isoString) => {
    try {
      return timeFormatter.format(new Date(isoString));
    } catch {
      return "—";
    }
  };

  const formatDateShort = (isoString) => {
    try {
      return dateShortFormatter.format(new Date(isoString));
    } catch {
      return "—";
    }
  };

  const selectedSlotLabel = useMemo(() => {
    if (!selectedSlot) return "";
    const slot = slots.find((s) => s.slot_id === selectedSlot);
    if (!slot) return "";
    return `${formatDateShort(slot.slot_from)} · ${formatTime(slot.slot_from)} - ${formatTime(slot.slot_to)} (${activeTZ.label})`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlot, slots, activeTZ.label, activeTZ.iana]);

  const handleManualRefresh = () => fetchSlots(selectedDate, { showLoader: false });

  const canProceed = Boolean(selectedSlot);

  const openModal = () => {
    if (!canProceed) return;
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  // Close modal on Esc
  useEffect(() => {
    if (!isModalOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isModalOpen]);

  // Close three-dots menu on click outside
  useEffect(() => {
    if (!menuOpenSlotId) return;
    const handleClickOutside = () => setMenuOpenSlotId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [menuOpenSlotId]);

  // Close edit modal on Esc
  useEffect(() => {
    if (!editSlotId) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeEditModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [editSlotId]);

  // Close password prompt on Esc
  useEffect(() => {
    if (!passwordPrompt) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setPasswordPrompt(null);
        setPasswordInput("");
        setPasswordError("");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [passwordPrompt]);

  // --- Self-service edit/release helpers ---
  const openEditModal = (slotId) => {
    const teller = storyTellers[slotId];
    if (!teller) return;
    setEditSlotId(slotId);
    setEditStorytellerData({
      storyteller_telugu: teller.storyteller_telugu || "",
      storyteller_english: teller.storyteller_english || "",
      writer_story_title: teller.writer_story_title || "",
      introduction: teller.introduction || "",
      whatsapp: teller.whatsapp || "",
      email: teller.email || "",
      photo_url: teller.photo_url || "",
      video_url: teller.video_url || "",
      story_length_mins: teller.story_length_mins,
    });
  };

  const closeEditModal = () => {
    setEditSlotId(null);
    setEditStorytellerData(null);
  };

  const handleRelease = async (slotId) => {
    if (!window.confirm("Are you sure you want to release this slot? Your registration will be removed.")) {
      return;
    }
    try {
      // Step 1: Delete the storyteller record (so re-registration won't hit unique constraint)
      const { error: deleteError } = await supabase
        .from("storytellers")
        .delete()
        .eq("slot_id", slotId);

      if (deleteError) throw deleteError;

      // Step 2: Mark the slot as available
      const { error: slotError } = await supabase
        .from("slots")
        .update({ is_booked: false })
        .eq("slot_id", slotId);

      if (slotError) throw slotError;
      await fetchSlots(selectedDate, { showLoader: false });
    } catch (err) {
      console.error("Error releasing slot:", err);
      alert("Failed to release slot: " + (err?.message || "Unknown error"));
    }
  };

  const verifyPassword = () => {
    const teller = storyTellers[passwordPrompt.slotId];
    if (!teller || !teller.storyteller_english) {
      setPasswordError("Unable to verify. Storyteller data not found.");
      return;
    }
    // Password = first name in English, lowercase
    const firstName = teller.storyteller_english.trim().split(/\s+/)[0].toLowerCase();
    if (passwordInput.trim().toLowerCase() === firstName) {
      // Password correct
      if (passwordPrompt.action === "edit") {
        openEditModal(passwordPrompt.slotId);
      } else if (passwordPrompt.action === "release") {
        handleRelease(passwordPrompt.slotId);
      }
      setPasswordPrompt(null);
      setPasswordInput("");
      setPasswordError("");
    } else {
      setPasswordError("Incorrect password. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 px-6 py-10 flex flex-col items-center">
      {/* Cover Image */}
      <img
        src={process.env.PUBLIC_URL + "/kathakala_utsavam_cover.png"}
        alt="Kathakala Utsavam Cover"
        className="w-full max-w-5xl rounded-lg shadow-md mb-4 object-cover"
        style={{ aspectRatio: "820 / 312" }}
      />

      {/* Title - Telugu & English on one line */}
      <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 text-center">
        ప్రపంచ కథాకళ ఉత్సవం! — World Storytelling Festival-2026
      </h1>

      {/* Sessions Link */}
      <button
        onClick={() => navigate("/sessions")}
        className="mb-6 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-lg shadow-md hover:from-orange-600 hover:to-red-600 transition-all text-base"
      >
        View Kathakala Utsavam Sessions →
      </button>

      <div className="w-full max-w-9xl bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
          {/* Date dropdown + availability info (inline) */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="shrink-0">
              <label className="block text-gray-700 font-medium mb-2">Select a Date:</label>
              <select
                className="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              >
                <option value="2026-03-21">Utsavam Day 1 - USA/Canada Start - March 21</option>
                <option value="2026-03-22">Utsavam Day 2 - Bharat Start - March 22</option>
              </select>
            </div>
            {!isLoading && slots.length > 0 && (
              <div className="flex items-center gap-4 pb-1 flex-wrap">
                <span className="text-base text-gray-700 whitespace-nowrap">
                  <span className="font-semibold text-green-700">{slots.filter(s => !s.is_booked).length}</span> available of <span className="font-semibold">{slots.length}</span> slots
                </span>
                <label className="flex items-center gap-1.5 text-base text-gray-700 cursor-pointer select-none whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={showAvailableOnly}
                    onChange={(e) => setShowAvailableOnly(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Show available only
                </label>
                <span className="text-base font-bold text-gray-800">Please pay attention to the timezone as you pick your slots</span>
              </div>
            )}
          </div>

          {/* Right controls: Toggle + Refresh + Selected + Proceed */}
          <div className="flex flex-wrap gap-3 items-center justify-start md:justify-end">
            {/* CST/IST toggle */}
            <div className="flex items-center gap-2 border rounded-md p-1 bg-white">
              <button
                onClick={() => setTzKey("CST")}
                className={`px-3 py-2 rounded-md text-sm font-semibold transition ${
                  tzKey === "CST"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
                type="button"
              >
                CST
              </button>
              <button
                onClick={() => setTzKey("IST")}
                className={`px-3 py-2 rounded-md text-sm font-semibold transition ${
                  tzKey === "IST"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
                type="button"
              >
                IST
              </button>
            </div>

            {/* Refresh */}
            <button
              onClick={handleManualRefresh}
              className="px-4 py-3 rounded-md border bg-white hover:bg-gray-50 text-gray-700"
              disabled={isLoading || isRefreshing}
              title="Refresh slots"
              type="button"
            >
              {isRefreshing ? "Refreshing..." : "Refresh"}
            </button>

            {/* Selected + Proceed */}
            {selectedSlot && (
              <>
                <div className="text-sm text-gray-700 bg-gray-100 border rounded-md px-3 py-2">
                  <span className="font-semibold">Selected:</span> {selectedSlotLabel}
                </div>

                <button
                  onClick={openModal}
                  className="px-5 py-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50"
                  disabled={!canProceed}
                  type="button"
                >
                  Proceed
                </button>
              </>
            )}
          </div>
        </div>

        {errorMsg ? (
          <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded-md text-red-700">
            {errorMsg}
          </div>
        ) : null}

        {isLoading ? (
          <div className="py-10 text-center text-gray-600">Loading slots...</div>
        ) : (
          <>
            {/* Scroll hint (optional, subtle) */}
            {slots.length > 16 && (
              <div className="text-sm text-gray-500 mb-3">
                Scroll to view more time slots ↓
              </div>
            )}

            {/* Scrollable Time Slots container */}
            <div className="max-h-[520px] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {slots.length > 0 ? (
                  (showAvailableOnly ? slots.filter(s => !s.is_booked) : slots).map((slot) => {
                    const isSelected = selectedSlot === slot.slot_id;

                    return (
                      <div
                        key={slot.slot_id}
                        className={`p-4 border rounded-lg text-center cursor-pointer font-semibold text-gray-700 transition-all ${
                          slot.is_booked
                            ? "bg-red-300 cursor-not-allowed relative"
                            : isSelected
                            ? "bg-orange-300"
                            : "bg-green-300 hover:bg-green-400"
                        }`}
                        onClick={() => handleSlotSelect(slot)}
                        role="button"
                        tabIndex={0}
                      >
                        {/* Three-dots menu for booked slots */}
                        {slot.is_booked && (
                          <div className="absolute top-1 right-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuOpenSlotId(menuOpenSlotId === slot.slot_id ? null : slot.slot_id);
                              }}
                              className="p-1 rounded hover:bg-red-400 text-gray-700"
                              type="button"
                              title="Actions"
                            >
                              <BsThreeDotsVertical size={16} />
                            </button>

                            {menuOpenSlotId === slot.slot_id && (
                              <div className="absolute right-0 top-full mt-1 bg-white border rounded shadow-lg z-20 min-w-[100px]">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuOpenSlotId(null);
                                    setPasswordPrompt({ slotId: slot.slot_id, action: "edit" });
                                    setPasswordInput("");
                                    setPasswordError("");
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuOpenSlotId(null);
                                    setPasswordPrompt({ slotId: slot.slot_id, action: "release" });
                                    setPasswordInput("");
                                    setPasswordError("");
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                                >
                                  Release
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        <p className="font-semibold text-gray-700">
                          {formatDateShort(slot.slot_from)} &middot; {activeTZ.label}
                        </p>
                        <p>
                          {formatTime(slot.slot_from)} - {formatTime(slot.slot_to)}
                        </p>

                        {slot.is_booked && storyTellers[slot.slot_id]?.storyteller_telugu && (
                            <p className="text-sm text-blue-800 font-medium mt-1">
                                {storyTellers[slot.slot_id].storyteller_telugu}
                                <span className="text-xs text-gray-700 font-semibold ml-2">
                                (ID: {slot.slot_id})
                                </span>
                            </p>
                        )}

                        <p className="text-sm font-semibold mt-1">
                          {slot.is_booked ? (
                            <span className="text-red-700">Booked</span>
                          ) : (
                            <span className="text-green-800">Available</span>
                          )}
                        </p>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500 text-center col-span-full">
                    No slots available for this date.
                  </p>
                )}
              </div>
            </div>

            {/* Color Legend */}
            <div className="mt-6 text-center">
              <p className="text-gray-700 font-medium mb-2">Color Legend:</p>
              <div className="flex justify-center flex-wrap gap-4">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-green-300 border border-gray-500"></div>
                  <p>Available</p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-red-300 border border-gray-500"></div>
                  <p>Booked</p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-orange-300 border border-gray-500"></div>
                  <p>Selected</p>
                </div>
              </div>
            </div>

            {/* Helpful hint */}
            {!selectedSlot ? (
              <div className="mt-8 text-center text-gray-600">
                Please select an <span className="font-semibold">Available</span> slot to continue.
              </div>
            ) : null}
          </>
        )}
      </div>

    {/* =========================
    MODAL (Form Popup)
   ========================= */}
        {isModalOpen && (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
            aria-modal="true"
            role="dialog"
        >
            {/* Overlay */}
            <div
            className="absolute inset-0 bg-black/50"
            onClick={closeModal}
            />

            {/* Modal Card */}
            <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Header (sticky) */}
            <div className="sticky top-0 z-10 bg-white border-b p-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                <h3 className="text-lg sm:text-xl font-bold text-gray-800">
                    Register for Storytelling
                </h3>

                <p className="text-sm text-gray-600 mt-1 break-words">
                    <span className="font-semibold">Selected Slot:</span>{" "}
                    {selectedSlotLabel}
                </p>
                </div>

                <button
                onClick={closeModal}
                className="shrink-0 px-3 py-2 rounded-md border bg-white hover:bg-gray-50 text-gray-700"
                type="button"
                >
                Close
                </button>
            </div>

            {/* Body (scrollable) */}
            <div className="p-4 overflow-y-auto max-h-[80vh] sm:max-h-[75vh]">
                <Form
                selectedSlot={selectedSlot}
                onSuccess={async () => {
                    closeModal();
                    await fetchSlots(selectedDate, { showLoader: false });
                    setSelectedSlot(null);
                }}
                onClose={closeModal}
                />
            </div>
            </div>
        </div>
        )}

        {/* ===========================
            PASSWORD PROMPT MODAL
        =========================== */}
        {passwordPrompt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" aria-modal="true" role="dialog">
            <div className="absolute inset-0 bg-black/50" onClick={() => { setPasswordPrompt(null); setPasswordInput(""); setPasswordError(""); }} />
            <div className="relative bg-white rounded-xl shadow-2xl border border-gray-200 p-6 w-full max-w-sm">
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                {passwordPrompt.action === "edit" ? "Edit Registration" : "Release Slot"}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Enter your first name (in English, lowercase) to continue.
              </p>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") verifyPassword(); }}
                placeholder="Enter first name"
                className="w-full p-3 border rounded-md mb-3 focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              {passwordError && (
                <p className="text-red-600 text-sm mb-3">{passwordError}</p>
              )}
              <div className="flex gap-3">
                <button
                  onClick={verifyPassword}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 font-semibold"
                  type="button"
                >
                  Verify
                </button>
                <button
                  onClick={() => { setPasswordPrompt(null); setPasswordInput(""); setPasswordError(""); }}
                  className="px-4 py-2 rounded-md border bg-white hover:bg-gray-50 text-gray-700"
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ===========================
            EDIT REGISTRATION MODAL
        =========================== */}
        {editSlotId && editStorytellerData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" aria-modal="true" role="dialog">
            <div className="absolute inset-0 bg-black/50" onClick={closeEditModal} />
            <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="sticky top-0 z-10 bg-white border-b p-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800">Edit Registration</h3>
                  <p className="text-sm text-gray-600 mt-1">Slot ID: {editSlotId}</p>
                </div>
                <button
                  onClick={closeEditModal}
                  className="shrink-0 px-3 py-2 rounded-md border bg-white hover:bg-gray-50 text-gray-700"
                  type="button"
                >
                  Close
                </button>
              </div>
              {/* Body */}
              <div className="p-4 overflow-y-auto max-h-[80vh] sm:max-h-[75vh]">
                <Form
                  selectedSlot={editSlotId}
                  editMode={true}
                  initialData={editStorytellerData}
                  onSuccess={async () => {
                    closeEditModal();
                    await fetchSlots(selectedDate, { showLoader: false });
                  }}
                  onClose={closeEditModal}
                />
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default Register;